import React, { useState, useEffect } from 'react';
import { HiOutlineShoppingBag, HiCheck, HiMinus, HiPlus } from 'react-icons/hi2';
import Icon from '../../utils/Icon';
import { Product } from '../../models/Product';
import { useCart } from '../../context/CartContext';
import { WEIGHT_OPTIONS, calcItemPrice } from '../../context/CartContext';
import {
  Card,
  ImageWrapper,
  CardBadge,
  ProductImage,
  CardContent,
  ProductName,
  ProductDescription,
  WeightSelector,
  WeightPill,
  QuantityRow,
  QtyBtn,
  QtyVal,
  CardFooter,
  ProductPrice,
  AddToCartButton,
  AddedFeedback,
} from './styles';

interface ProductCardProps {
  product: Product;
  index: number;
}

const formatPrice = (price: number): string =>
  price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ProductCard: React.FC<ProductCardProps> = ({ product, index }) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const [weight, setWeight] = useState(500);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (added) {
      const timer = setTimeout(() => setAdded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [added]);

  const unitPrice = product.price ? calcItemPrice(product.price, weight) : 0;

  const handleAddToCart = () => {
    addToCart(product, qty, weight);
    setAdded(true);
    setQty(1);
  };

  return (
    <Card style={{ animationDelay: `${index * 0.1}s` }} role="article">
      <ImageWrapper>
        <CardBadge>Artesanal</CardBadge>
        <ProductImage
          src={product.image}
          alt={`${product.name} — doce artesanal`}
          loading="lazy"
          decoding="async"
        />
      </ImageWrapper>
      <CardContent>
        <ProductName>{product.name}</ProductName>
        <ProductDescription>{product.description}</ProductDescription>

        <WeightSelector role="group" aria-label="Selecionar gramatura">
          {WEIGHT_OPTIONS.map((w) => (
            <WeightPill
              key={w}
              $active={weight === w}
              onClick={() => setWeight(w)}
              aria-pressed={weight === w}
              type="button"
            >
              {w}g
            </WeightPill>
          ))}
        </WeightSelector>

        <QuantityRow>
          <QtyBtn
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            aria-label="Diminuir quantidade"
            type="button"
          >
            <Icon icon={HiMinus} />
          </QtyBtn>
          <QtyVal aria-label={`Quantidade: ${qty}`}>{qty}</QtyVal>
          <QtyBtn
            onClick={() => setQty((q) => q + 1)}
            aria-label="Aumentar quantidade"
            type="button"
          >
            <Icon icon={HiPlus} />
          </QtyBtn>
        </QuantityRow>

        <CardFooter>
          {product.price && <ProductPrice>{formatPrice(unitPrice)}</ProductPrice>}
          {added ? (
            <AddedFeedback aria-live="polite">
              <Icon icon={HiCheck} />
              Adicionado
            </AddedFeedback>
          ) : (
            <AddToCartButton
              onClick={handleAddToCart}
              aria-label={`Adicionar ${qty}x ${product.name} (${weight}g) ao carrinho`}
              type="button"
            >
              <Icon icon={HiOutlineShoppingBag} />
              Adicionar
            </AddToCartButton>
          )}
        </CardFooter>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
